import { createAnalyticEvent, trackAuthenticationTelemetry } from '../../shared/analytics/services';
import { TrackingAction, WEB_NAVIGATION_MAP } from '@rapid-recovery-agency-inc/insightt-shared';
import { isSsoPathActive } from './rollout';
import {
  AUTHENTICATION_OUTCOME_DURATION_BUCKETS,
  type AuthenticationOutcomeDurationBucket,
} from './duration-bucket';

export const trackLogin = (role: string) => {
  const trackEvent = createAnalyticEvent(
    TrackingAction.LOGIN,
    WEB_NAVIGATION_MAP.AUTHENTICATION.features.LOGIN.views.LOGIN,
  );
  trackEvent({ $role: role });
};

export const trackLogout = createAnalyticEvent(TrackingAction.LOGIN, WEB_NAVIGATION_MAP.TOP_BAR.features.SIGN_OUT);

/**
 * Closed, redacted authentication-outcome events (FR-025, FR-026, FR-046; T015, T061, T072, T073).
 *
 * The event is a Dashboard-owned, bounded schema (plan.md Constitution III row), so it neither
 * re-declares a shared `TrackingAction` member nor a `WEB_NAVIGATION_MAP` entry: the event name is
 * the Dashboard constant below, and delivery goes through
 * `shared/analytics/services.ts`'s `trackAuthenticationTelemetry`, which sends only the FR-046
 * fields. {@link reportAuthenticationOutcome} is the entry point for production code: it never
 * throws, so telemetry can never break authentication (FR-025).
 */
export const AUTHENTICATION_OUTCOME_EVENT_NAME = 'authentication_outcome';

/** Every allowed `stage` value (FR-046, closed). */
export const AUTHENTICATION_OUTCOME_STAGES = [
  'gateway',
  'handoff',
  'callback',
  'renewal',
  'legacy',
  'recovery',
  'unauthorized',
  'logout',
] as const;
export type AuthenticationOutcomeStage = (typeof AUTHENTICATION_OUTCOME_STAGES)[number];

/** Every allowed `outcome` value (FR-046, closed). */
export const AUTHENTICATION_OUTCOME_OUTCOMES = ['started', 'succeeded', 'failed', 'cancelled'] as const;
export type AuthenticationOutcomeOutcome = (typeof AUTHENTICATION_OUTCOME_OUTCOMES)[number];

/** Every allowed `reason` value (FR-046, closed). */
export const AUTHENTICATION_OUTCOME_REASONS = [
  'none',
  'validation',
  'policy',
  'timeout',
  'unavailable',
  'malformed',
  'mismatch',
  'expired',
  'replayed',
  'browser_blocked',
  'unauthorized',
  'partial_logout',
] as const;
export type AuthenticationOutcomeReason = (typeof AUTHENTICATION_OUTCOME_REASONS)[number];

// The duration buckets live in `duration-bucket.ts` so boot code can use them without this module's
// analytics imports; re-exported here as part of the telemetry contract.
export {
  AUTHENTICATION_OUTCOME_DURATION_BUCKETS,
  bucketAuthenticationOutcomeDuration,
  type AuthenticationOutcomeDurationBucket,
} from './duration-bucket';

/**
 * Every allowed `interactionType` value, as `contracts/telemetry.md` defines them: `user` for an
 * explicit user action (a gateway submission, a Sign Out click) and `automatic` for everything the
 * system starts (silent renewal, unauthorized cleanup). SC-001's denominator filters on `user`.
 */
export const AUTHENTICATION_OUTCOME_INTERACTION_TYPES = ['user', 'automatic'] as const;
export type AuthenticationOutcomeInteractionType = (typeof AUTHENTICATION_OUTCOME_INTERACTION_TYPES)[number];

/**
 * The closed `AUTHENTICATION_OUTCOME` event shape (FR-046, data-model.md §10). Optional fields
 * are limited to exactly these three; nothing else may be added without a spec change. There is
 * intentionally no index signature, so any object literal with a key outside this set fails a
 * TypeScript excess-property check at its construction site in addition to the runtime rejection
 * `buildAuthenticationOutcomeEvent` performs below.
 */
export interface AuthenticationOutcomeEvent {
  readonly stage: AuthenticationOutcomeStage;
  readonly outcome: AuthenticationOutcomeOutcome;
  readonly reason: AuthenticationOutcomeReason;
  readonly contractVersion?: number;
  readonly durationBucket?: AuthenticationOutcomeDurationBucket;
  readonly interactionType?: AuthenticationOutcomeInteractionType;
}

const ALLOWED_AUTHENTICATION_OUTCOME_KEYS: readonly (keyof AuthenticationOutcomeEvent)[] = [
  'stage',
  'outcome',
  'reason',
  'contractVersion',
  'durationBucket',
  'interactionType',
];

/**
 * Raised whenever a caller attempts to build an `AUTHENTICATION_OUTCOME` event carrying a value
 * outside FR-046's closed enums/field set, or a value that looks like an identifier, credential,
 * raw error, or URL. This is the runtime half of "closed" — it exists so a caller that bypasses
 * the `AuthenticationOutcomeEvent` type (e.g. via `any`, JSON parsing, or a spread from an
 * untyped source) still cannot get a redaction-violating payload out of this mapper.
 */
export class InvalidAuthenticationOutcomeEventError extends Error {
  constructor(message: string) {
    super(`Invalid AUTHENTICATION_OUTCOME event: ${message}`);
    this.name = 'InvalidAuthenticationOutcomeEventError';
  }
}

/** Matches an email address, a bearer/URL-shaped value, or a long opaque identifier/token. */
const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const URL_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;
const LONG_OPAQUE_TOKEN_PATTERN = /^[A-Za-z0-9\-_.]{20,}$/;

function assertNotSensitiveString(value: string, field: string): void {
  if (EMAIL_PATTERN.test(value)) {
    throw new InvalidAuthenticationOutcomeEventError(`field "${field}" looks like an email address`);
  }
  if (URL_PATTERN.test(value)) {
    throw new InvalidAuthenticationOutcomeEventError(`field "${field}" looks like a URL`);
  }
  if (LONG_OPAQUE_TOKEN_PATTERN.test(value)) {
    throw new InvalidAuthenticationOutcomeEventError(
      `field "${field}" looks like an identifier, token, or credential`,
    );
  }
}

/**
 * Validates and returns a closed, redacted `AUTHENTICATION_OUTCOME` event. Throws
 * `InvalidAuthenticationOutcomeEventError` for any unapproved key, any value outside FR-046's
 * enums, or any string value that looks like an identifier, credential, raw error, or URL.
 */
export function buildAuthenticationOutcomeEvent(input: AuthenticationOutcomeEvent): AuthenticationOutcomeEvent {
  if (input === null || typeof input !== 'object') {
    throw new InvalidAuthenticationOutcomeEventError('input must be an object');
  }

  const providedKeys = Object.keys(input);
  const unknownKeys = providedKeys.filter(
    (key) => !ALLOWED_AUTHENTICATION_OUTCOME_KEYS.includes(key as keyof AuthenticationOutcomeEvent),
  );
  if (unknownKeys.length > 0) {
    throw new InvalidAuthenticationOutcomeEventError(`unapproved field(s): ${unknownKeys.join(', ')}`);
  }

  if (!AUTHENTICATION_OUTCOME_STAGES.includes(input.stage)) {
    throw new InvalidAuthenticationOutcomeEventError(`unknown stage "${String(input.stage)}"`);
  }
  if (!AUTHENTICATION_OUTCOME_OUTCOMES.includes(input.outcome)) {
    throw new InvalidAuthenticationOutcomeEventError(`unknown outcome "${String(input.outcome)}"`);
  }
  if (!AUTHENTICATION_OUTCOME_REASONS.includes(input.reason)) {
    throw new InvalidAuthenticationOutcomeEventError(`unknown reason "${String(input.reason)}"`);
  }

  if (input.contractVersion !== undefined) {
    if (!Number.isInteger(input.contractVersion) || input.contractVersion < 1) {
      throw new InvalidAuthenticationOutcomeEventError('contractVersion must be a positive integer');
    }
  }
  if (
    input.durationBucket !== undefined &&
    !AUTHENTICATION_OUTCOME_DURATION_BUCKETS.includes(input.durationBucket)
  ) {
    throw new InvalidAuthenticationOutcomeEventError(`unknown durationBucket "${String(input.durationBucket)}"`);
  }
  if (
    input.interactionType !== undefined &&
    !AUTHENTICATION_OUTCOME_INTERACTION_TYPES.includes(input.interactionType)
  ) {
    throw new InvalidAuthenticationOutcomeEventError(`unknown interactionType "${String(input.interactionType)}"`);
  }

  // Defense in depth: even though the enum checks above already constrain stage/outcome/reason to
  // exact approved literals (so an email/credential/URL could never equal one), re-validate every
  // provided string value. This guards a caller that constructs `input` via `as unknown as
  // AuthenticationOutcomeEvent` or another type-erasing cast instead of going through this file.
  for (const key of providedKeys) {
    const value = (input as unknown as Record<string, unknown>)[key];
    if (typeof value === 'string') {
      assertNotSensitiveString(value, key);
    }
  }

  return {
    stage: input.stage,
    outcome: input.outcome,
    reason: input.reason,
    ...(input.contractVersion !== undefined && { contractVersion: input.contractVersion }),
    ...(input.durationBucket !== undefined && { durationBucket: input.durationBucket }),
    ...(input.interactionType !== undefined && { interactionType: input.interactionType }),
  };
}

/** Sends one validated event; throws for an invalid event (tests and {@link reportAuthenticationOutcome} use it). */
export function trackAuthenticationOutcome(input: AuthenticationOutcomeEvent): void {
  const event = buildAuthenticationOutcomeEvent(input);
  trackAuthenticationTelemetry(AUTHENTICATION_OUTCOME_EVENT_NAME, {
    $stage: event.stage,
    $outcome: event.outcome,
    $reason: event.reason,
    ...(event.contractVersion !== undefined && { $contract_version: event.contractVersion }),
    ...(event.durationBucket !== undefined && { $duration_bucket: event.durationBucket }),
    ...(event.interactionType !== undefined && { $interaction_type: event.interactionType }),
  });
}

/** Reports an outcome without ever throwing: a bad event or a failed transport is dropped (FR-025). */
export function reportAuthenticationOutcome(input: AuthenticationOutcomeEvent): void {
  // No SSO on this page (gate off, no SSO session, no returning sign-in): no new events, so analytics
  // match the pre-change baseline (FR-035; T103; #4193 FR-006).
  if (!isSsoPathActive()) return;
  try {
    trackAuthenticationOutcome(input);
  } catch {
    // Telemetry must never interrupt authentication.
  }
}

const HOSTILE_CALLBACK_REASONS: readonly AuthenticationOutcomeReason[] = [
  'malformed',
  'mismatch',
  'expired',
  'replayed',
];

/**
 * Reasons that indicate a technically-valid callback whose subsequent Backend session
 * establishment nonetheless reached one of the three named recoverable outcomes (spec.md: "a
 * valid callback whose subsequent Backend session establishment reaches a recoverable timeout,
 * unavailable, or unauthorized outcome").
 */
const RECOVERABLE_CALLBACK_REASONS: readonly AuthenticationOutcomeReason[] = [
  'timeout',
  'unavailable',
  'unauthorized',
];

/**
 * SC-001 denominator: "Count each current, user-initiated SSO operation generation exactly once
 * when the authoritative route decision permits its first centralized handoff." That first
 * centralized handoff is the `handoff`-stage `started` event with no failure reason yet — one per
 * generation by the one-start cardinality rule (see `createAuthenticationEpisode`). Internal
 * retries/renewal/replay never emit a new `handoff started`, so they are excluded automatically
 * by construction rather than by a special case here.
 */
export function isSc001DenominatorEvent(event: AuthenticationOutcomeEvent): boolean {
  return event.stage === 'handoff' && event.outcome === 'started' && event.reason === 'none';
}

/**
 * SC-001 numerator: "Count the denominator entries that establish the trusted Backend-authorized
 * dashboard session and reach an allowed destination." That is represented by the generation's
 * terminal event being a successful callback (the stage at which the Backend session is actually
 * established per data-model.md §7/§10) with no failure reason.
 */
export function isSc001NumeratorEvent(event: AuthenticationOutcomeEvent): boolean {
  return event.stage === 'callback' && event.outcome === 'succeeded' && event.reason === 'none';
}

/**
 * SC-002 denominator: "every callback operation that passes all required OIDC flow and
 * token-metadata validation and is therefore valid" — i.e. a `callback` terminal event whose
 * reason is not one of the hostile/invalid reasons SC-003 governs instead.
 */
export function isSc002DenominatorEvent(event: AuthenticationOutcomeEvent): boolean {
  if (event.stage !== 'callback') {
    return false;
  }
  if (event.outcome === 'succeeded') {
    return event.reason === 'none';
  }
  if (event.outcome === 'failed') {
    return !HOSTILE_CALLBACK_REASONS.includes(event.reason);
  }
  return false;
}

/**
 * SC-002 numerator: a denominator entry that, within 3,000ms of monotonic callback-route entry
 * (`durationBucket === 'le_3s'`), either establishes the session (`succeeded`) or visibly renders
 * one of the three named recoverable outcomes (`failed` with `timeout`/`unavailable`/`unauthorized`).
 */
export function isSc002NumeratorEvent(event: AuthenticationOutcomeEvent): boolean {
  if (!isSc002DenominatorEvent(event) || event.durationBucket !== 'le_3s') {
    return false;
  }
  return (
    event.outcome === 'succeeded' ||
    (event.outcome === 'failed' && RECOVERABLE_CALLBACK_REASONS.includes(event.reason))
  );
}

/**
 * Enforces data-model.md §10's cardinality rule — "At most one start and one terminal event occur
 * per generation" — for one authentication operation generation. This is a purely local,
 * in-memory guard (no generation identifier is ever attached to an emitted event); it exists so
 * callers cannot accidentally double-count a generation's SC-001/SC-002 contribution.
 */
export function createAuthenticationEpisode() {
  let startRecorded = false;
  let terminalRecorded = false;
  const events: AuthenticationOutcomeEvent[] = [];

  return {
    recordStart(event: AuthenticationOutcomeEvent): AuthenticationOutcomeEvent {
      if (event.outcome !== 'started') {
        throw new InvalidAuthenticationOutcomeEventError('recordStart requires an outcome of "started"');
      }
      if (startRecorded) {
        throw new InvalidAuthenticationOutcomeEventError('a start event was already recorded for this episode');
      }
      const built = buildAuthenticationOutcomeEvent(event);
      startRecorded = true;
      events.push(built);
      return built;
    },
    recordTerminal(event: AuthenticationOutcomeEvent): AuthenticationOutcomeEvent {
      if (event.outcome === 'started') {
        throw new InvalidAuthenticationOutcomeEventError(
          'recordTerminal requires a terminal outcome ("succeeded", "failed", or "cancelled")',
        );
      }
      if (terminalRecorded) {
        throw new InvalidAuthenticationOutcomeEventError('a terminal event was already recorded for this episode');
      }
      const built = buildAuthenticationOutcomeEvent(event);
      terminalRecorded = true;
      events.push(built);
      return built;
    },
    getEvents(): readonly AuthenticationOutcomeEvent[] {
      return events;
    },
  };
}
